import { useCallback, useEffect, useMemo } from "react"
import { Navigate } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import { Box, Stack, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { LOGIN_ROUTE } from "../../../constants/routes"
import { ErrorDisplay } from "../../../core/api/errorTypes"
import { RefetchIntervals } from "../../../core/api/refetchIntervals"
import { useApiQuery } from "../../../core/api/useApiQuery"
import { selectEffectiveCompanyId } from "../../context/contextSelectors"
import { LocalizedStrict } from "../../localization/components/AppLocalized"
import type { TranslationKey } from "../../localization/translations"
import { useDateTime } from "../../localization/useDateTime"
import { selectAuthToken, selectMerchantUid } from "../../login/loginSelectors"
import {
    MenuPlaningQuery,
    MenusListResponse,
    fetchExtMenusList,
} from "../../merchant/merchant/menuPlanning/menuPlanningApi"
import { SignageType } from "../../merchant/merchant/menuPlanning/menuPlanningTypes"
import { useAppSelector } from "../../store/storeHooks"
import {
    SupplierMerchantQuery,
    fetchSupplierMerchantDetails,
} from "../../supplier/admin/merchant/merchants/supplierMerchantsApi"

import { MenuDisplayFeatured } from "./MenuDisplayFeatured"
import { MenuDisplayHeader } from "./MenuDisplayHeader"
import { MenuDisplaySidebar } from "./MenuDisplaySidebar"
import {
    MENU_DISPLAY_BODY_FONT,
    MENU_DISPLAY_DISPLAY_FONT,
    MENU_DISPLAY_SIDEBAR_MAX_ITEMS,
    MenuDisplayData,
} from "./menuDisplayData"
import { ConnectivityStatus, MenuDisplayConnectivityQuery, diagnoseConnectivity } from "./menuDisplayConnectivity"
import { readMenuDisplayCache, writeMenuDisplayCache } from "./menuDisplayStorage"
import { useBrowserOnline } from "./useBrowserOnline"
import { useMenuCarousel } from "./useMenuCarousel"

// What the indicator says per diagnosis. ONLINE here means the backend is reachable while the
// menu request itself keeps failing, so it maps to the neutral "could not update" message.
const STATUS_MESSAGE_IDS: Record<ConnectivityStatus, TranslationKey> = {
    [ConnectivityStatus.LOCAL_OFFLINE]: "public-menu-display-status-offline",
    [ConnectivityStatus.SERVER_DOWN]: "public-menu-display-status-server-down",
    [ConnectivityStatus.ONLINE]: "public-menu-display-status-refresh-failed",
}

// The stage is a 16:9 box scaled to fill the viewport; 1em tracks the stage size (1em = 16px on a
// 1920-wide stage), so the whole design scales proportionally on any screen.
const STAGE_WIDTH = "min(100vw, calc(100svh * 16 / 9))"
const STAGE_HEIGHT = "min(100svh, calc(100vw * 9 / 16))"
const STAGE_FONT_SIZE = "min(.8333vw, 1.4815svh)"

const useStyles = makeStyles()((theme) => ({
    page: {
        width: "100vw",
        height: "100svh",
        overflow: "hidden",
        backgroundColor: theme.palette.common.white,
    },
    stage: {
        position: "relative",
        width: STAGE_WIDTH,
        height: STAGE_HEIGHT,
        overflow: "hidden",
        backgroundColor: theme.palette.common.white,
        fontFamily: MENU_DISPLAY_BODY_FONT,
        fontSize: STAGE_FONT_SIZE,
        letterSpacing: 0,
    },
    content: {
        flex: "1 1 auto",
        minHeight: 0,
        overflow: "hidden",
    },
    emptyMessage: {
        fontFamily: MENU_DISPLAY_DISPLAY_FONT,
        fontWeight: 600,
        fontSize: "4em",
        textTransform: "uppercase",
        color: theme.palette.tailwind.slate[400],
    },
    statusIndicator: {
        position: "absolute",
        left: "50%",
        bottom: "2.1em",
        transform: "translateX(-50%)",
    },
    statusDot: {
        flexShrink: 0,
        width: "1em",
        height: "1em",
        borderRadius: "50%",
        backgroundColor: theme.palette.tailwind.red[500],
    },
    statusText: {
        fontWeight: 600,
        fontSize: "1.5em",
        whiteSpace: "nowrap",
        color: theme.palette.tailwind.red[500],
    },
}))

const selectMenuDisplayData = (data: MenusListResponse, today: string | undefined): MenuDisplayData => {
    // The same menus the intranet week menu shows, narrowed to today.
    const todayMenus = (data.menuPlanning?.weeks ?? [])
        .flatMap((week) => week.dailyMenus)
        .filter(
            (dailyMenu) =>
                dailyMenu.isActive &&
                dailyMenu.publishDate === today &&
                dailyMenu.signageTypes?.includes(SignageType.WEB_MENU),
        )

    const items = todayMenus.flatMap((dailyMenu) =>
        dailyMenu.dishes.map((dish, dishIndex) => ({
            id: `${dailyMenu.id}-${dishIndex}`,
            tag: dish.productDescription?.marketingTag,
            name: dish.name,
            description: dish.description,
            allergens: dish.productDescription?.allergens?.map((allergen) => allergen.name) ?? [],
            price: dish.price,
            imageUrl: dish.productPhoto?.url,
        })),
    )

    const content = data.menuPlanning?.content
    return { title: content?.title, logoUrl: content?.logoUrl, menuQrUrl: content?.menuQrUrl, items }
}

export function MenuDisplayPage() {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const queryClient = useQueryClient()

    const authToken = useAppSelector(selectAuthToken)
    // The screen is opened through the device-secret bridge link, so the session is a device
    // session and its merchant drives the display (same setup as the canteen week menu).
    const merchantId = useAppSelector(selectEffectiveCompanyId)

    const now = dateTimeFactory.now()
    const today = now.toISODate() ?? undefined
    const from = now.startOf("week").toISODate() ?? undefined
    const to = now.endOf("week").toISODate() ?? undefined

    // The device session carries the merchant uid from authenticate-by-type, so this flow never
    // calls Get merchant details (a device session cannot); only the admin preview session still
    // resolves the uid — the same setup as the canteen week menu.
    const sessionMerchantUid = useAppSelector(selectMerchantUid)
    const merchantUidResult = useApiQuery(fetchSupplierMerchantDetails, {
        queryName: SupplierMerchantQuery.DETAILS,
        dependencies: { merchantId: merchantId ?? 0 },
        enabled: sessionMerchantUid === undefined && Boolean(merchantId),
        errorDisplay: ErrorDisplay.NONE,
        select: (data) => data.merchant.uid,
    })
    const merchantUid = sessionMerchantUid ?? merchantUidResult.data ?? ""

    // Stable identity so the transform (and the cache write below) only reruns when data changes,
    // not on every carousel tick.
    const selectForToday = useCallback((data: MenusListResponse) => selectMenuDisplayData(data, today), [today])

    // The same request as the canteen week menu (whole week); the select keeps only today's menus.
    const menusResult = useApiQuery(fetchExtMenusList, {
        queryName: MenuPlaningQuery.EXT_LIST,
        dependencies: { merchantUid, from, to },
        enabled: Boolean(merchantUid),
        // Failed refreshes must never break the screen: no snackbar, the last data stays on
        // display and the only signal is the discreet status indicator below.
        errorDisplay: ErrorDisplay.NONE,
        refetchInterval: RefetchIntervals.short,
        select: selectForToday,
    })

    // The last successful menu survives TV reboots; dishes are only reused for the same day,
    // while the date-independent branding (title, logo, QR) is restored either way. The cache is
    // keyed by the session merchant, which is known even while the uid lookup is unavailable.
    const cacheKey = merchantId !== undefined ? `${merchantId}` : undefined
    const cachedRecord = useMemo(() => (cacheKey ? readMenuDisplayCache(cacheKey) : undefined), [cacheKey])
    const cachedDisplay = useMemo(() => {
        if (!cachedRecord) return undefined
        return cachedRecord.forDate === today ? cachedRecord.data : { ...cachedRecord.data, items: [] }
    }, [cachedRecord, today])

    useEffect(() => {
        if (!cacheKey || !menusResult.data) return
        writeMenuDisplayCache(cacheKey, {
            savedAtMillis: dateTimeFactory.now().toMillis(),
            forDate: today,
            data: menusResult.data,
        })
    }, [cacheKey, menusResult.data, dateTimeFactory, today])

    const display = menusResult.data ?? cachedDisplay
    const items = display?.items ?? []

    // React Query clears the error state while a no-data query retries, so isError alone would
    // blink off for a moment on every poll; "failing" has to stick across those retry windows.
    // The uid lookup counts too: while it cannot load, the menu cannot refresh either.
    const isUidFailing = merchantUidResult.errorUpdatedAt > 0 && !merchantUidResult.isSuccess
    const isMenusFailing = menusResult.errorUpdatedAt > 0 && !menusResult.isSuccess
    const isMenuFailing = isUidFailing || isMenusFailing
    const isFirstLoad = (merchantUidResult.isInitialLoading || menusResult.isInitialLoading) && !isMenuFailing
    // "No menu published" is only a fact while today's menu is loaded and the refresh works;
    // while it fails, an empty screen means the menu could not be loaded.
    const isConfirmedEmpty = menusResult.isSuccess && !isMenuFailing

    const isBrowserOnline = useBrowserOnline()

    // Only probe while the menu refresh actually fails; the probe itself never throws, it returns
    // a classification (backend down vs. no internet) so the screen shows the right message.
    const connectivityResult = useApiQuery(diagnoseConnectivity, {
        queryName: MenuDisplayConnectivityQuery.STATUS,
        dependencies: {},
        enabled: Boolean(merchantId) && isMenuFailing && isBrowserOnline,
        errorDisplay: ErrorDisplay.NONE,
        refetchInterval: RefetchIntervals.short,
    })

    // A classification only means anything while the outage lasts: drop it once the menu query
    // recovers, so the next outage starts from a fresh diagnosis instead of the previous verdict.
    useEffect(() => {
        if (!isMenuFailing) {
            queryClient.removeQueries({ queryKey: [MenuDisplayConnectivityQuery.STATUS] })
        }
    }, [isMenuFailing, queryClient])

    const connectivity = !isBrowserOnline ? ConnectivityStatus.LOCAL_OFFLINE : connectivityResult.data
    const showStatus = Boolean(merchantId) && (isMenuFailing || !isBrowserOnline)
    // While the diagnosis is still pending, fall back to the neutral ONLINE message.
    const statusMessageId = showStatus ? STATUS_MESSAGE_IDS[connectivity ?? ConnectivityStatus.ONLINE] : undefined

    const { offsetIndex, activeIndex, animate } = useMenuCarousel(items.length)

    // The sidebar lists the upcoming dishes, starting right after the active one. With too few
    // dishes to fill the list that way, it just shows the whole menu without rotating.
    const sidebarItems = useMemo(() => {
        if (items.length <= MENU_DISPLAY_SIDEBAR_MAX_ITEMS) return items
        return [...items.slice(activeIndex + 1), ...items.slice(0, activeIndex)].slice(
            0,
            MENU_DISPLAY_SIDEBAR_MAX_ITEMS,
        )
    }, [items, activeIndex])

    // No session means the display was opened without its device-secret bridge link.
    if (!authToken) return <Navigate to={LOGIN_ROUTE} replace />

    return (
        <Stack alignItems="center" justifyContent="center" className={classes.page}>
            <Stack direction="column" className={classes.stage}>
                <MenuDisplayHeader title={display?.title} logoUrl={display?.logoUrl} />
                {items.length > 0 ? (
                    <Stack direction="row" spacing="3.5em" className={classes.content}>
                        <MenuDisplayFeatured
                            items={items}
                            offsetIndex={offsetIndex}
                            activeIndex={activeIndex}
                            animate={animate}
                        />
                        <MenuDisplaySidebar items={sidebarItems} menuQrUrl={display?.menuQrUrl} />
                    </Stack>
                ) : (
                    <Stack alignItems="center" justifyContent="center" className={classes.content}>
                        {!isFirstLoad &&
                            (isConfirmedEmpty ? (
                                <LocalizedStrict id="public-menu-display-empty-message">
                                    <Typography className={classes.emptyMessage}>
                                        No menu published for today
                                    </Typography>
                                </LocalizedStrict>
                            ) : (
                                <LocalizedStrict id="public-menu-display-unavailable-message">
                                    <Typography className={classes.emptyMessage}>
                                        The menu could not be loaded
                                    </Typography>
                                </LocalizedStrict>
                            ))}
                    </Stack>
                )}
                {statusMessageId && (
                    <Stack direction="row" alignItems="center" spacing=".75em" className={classes.statusIndicator}>
                        <Box className={classes.statusDot} />
                        <LocalizedStrict id={statusMessageId}>
                            <Typography className={classes.statusText}>No internet</Typography>
                        </LocalizedStrict>
                    </Stack>
                )}
            </Stack>
        </Stack>
    )
}
